// Lightweight wrapper around the Web Speech API for in-browser dictation.
// Returns { isSupported, isRecording, transcript, error, start, stop, reset }.
// Designed to plug into any text input — caller passes `onResult(finalText)`
// to start() and we append final results as they come in.
//
// Browser support today (Nov 2025):
//   ✅ Chrome / Edge desktop + Android
//   ✅ Safari macOS 14.5+ / iOS 14.5+
//   ❌ Firefox (no plans to ship)
// When unsupported, isSupported is false and the caller should hide its UI.

import { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '../services/analytics';

// Auto-stop after this many ms of complete silence to avoid runaway sessions.
const SILENCE_TIMEOUT_MS = 60 * 1000;

function getSpeechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function useVoiceDictation() {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const onResultRef = useRef(null);
  const onEndRef = useRef(null);
  const firedAnalyticsRef = useRef(false);

  const isSupported = !!getSpeechRecognition();

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      // Hit silence ceiling — politely stop the session.
      try { recognitionRef.current?.stop(); } catch (_) {}
    }, SILENCE_TIMEOUT_MS);
  }, []);

  const stop = useCallback(() => {
    clearSilenceTimer();
    try {
      recognitionRef.current?.stop();
    } catch (_) {}
    setIsRecording(false);
  }, []);

  const reset = useCallback(() => {
    setTranscript('');
    setError(null);
  }, []);

  const start = useCallback(({ onResult, onEnd } = {}) => {
    if (!isSupported) {
      setError('Speech recognition is not supported in this browser.');
      return;
    }
    onResultRef.current = onResult || null;
    onEndRef.current = onEnd || null;

    // If an instance exists, stop it cleanly before starting a new one.
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_) {}
    }

    const SpeechRecognition = getSpeechRecognition();
    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

    rec.onresult = (event) => {
      armSilenceTimer();
      let finalChunk = '';
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const text = result[0]?.transcript || '';
        if (result.isFinal) {
          finalChunk += text;
        } else {
          interim += text;
        }
      }
      if (finalChunk) {
        setTranscript((prev) => (prev ? prev + ' ' : '') + finalChunk.trim());
        if (!firedAnalyticsRef.current) {
          firedAnalyticsRef.current = true;
          try { trackEvent('hero_voice_dictation_used'); } catch (_) {}
        }
        if (onResultRef.current) {
          try { onResultRef.current(finalChunk.trim()); } catch (_) {}
        }
      }
      // Caller can use the interim text via onResult too if they want, but we
      // intentionally only emit on final to avoid choppy textarea updates.
      void interim;
    };

    rec.onerror = (event) => {
      const code = event?.error || 'unknown';
      // 'no-speech' and 'aborted' are non-fatal — surface a friendly hint.
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        setError('Microphone permission denied. Enable it in your browser settings.');
      } else if (code === 'no-speech') {
        setError(null); // benign
      } else {
        setError(`Voice input error: ${code}`);
      }
      setIsRecording(false);
      clearSilenceTimer();
    };

    rec.onend = () => {
      setIsRecording(false);
      clearSilenceTimer();
      if (onEndRef.current) {
        try { onEndRef.current(); } catch (_) {}
      }
    };

    try {
      rec.start();
      recognitionRef.current = rec;
      setIsRecording(true);
      setError(null);
      armSilenceTimer();
    } catch (err) {
      setError(err?.message || 'Failed to start voice input');
      setIsRecording(false);
    }
  }, [armSilenceTimer, isSupported]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSilenceTimer();
      try { recognitionRef.current?.stop(); } catch (_) {}
    };
  }, []);

  return { isSupported, isRecording, transcript, error, start, stop, reset };
}

export default useVoiceDictation;
